/**
 * Infinite scroll for collection product grids.
 * Uses a plain scroll/resize listener (instead of IntersectionObserver) so it
 * works regardless of custom-element display quirks or observer edge cases.
 * Falls back to normal pagination links if JS fails or is disabled (see the
 * <noscript> pagination rendered inside the section markup).
 */
class InfiniteScrollList extends HTMLElement {
  connectedCallback() {
    this.style.display = 'block';
    this.list = this.querySelector('#product-grid');
    this.sentinel = this.querySelector('.infinite-scroll__sentinel');
    this.spinner = this.querySelector('.infinite-scroll__spinner');
    this.loading = false;
    this.ticking = false;

    if (!this.sentinel || !this.list || !this.dataset.nextUrl) return;

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    // Check once immediately in case the sentinel is already on-screen
    // (e.g. a short grid on a tall viewport).
    this.checkPosition();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.checkPosition();
      this.ticking = false;
    });
  }

  checkPosition() {
    if (!this.sentinel || this.loading || !this.dataset.nextUrl) return;
    const rect = this.sentinel.getBoundingClientRect();
    const threshold = window.innerHeight + 800;
    if (rect.top <= threshold) {
      this.loadMore();
    }
  }

  buildFetchUrl(rawUrl) {
    const url = new URL(rawUrl, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);
    ['preview_theme_id', '_fd', '_ab', '_sc'].forEach((key) => {
      if (currentParams.has(key) && !url.searchParams.has(key)) {
        url.searchParams.set(key, currentParams.get(key));
      }
    });
    return url;
  }

  async loadMore() {
    if (this.loading || !this.dataset.nextUrl) return;
    this.loading = true;
    if (this.spinner) this.spinner.hidden = false;

    const fetchUrl = this.buildFetchUrl(this.dataset.nextUrl);

    try {
      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      const newGrid = doc.getElementById('product-grid');
      const newContainer = doc.querySelector('infinite-scroll-list');

      if (newGrid && this.list) {
        Array.from(newGrid.children).forEach((child) => this.list.appendChild(child));
      }

      const nextUrlRaw = newContainer ? newContainer.dataset.nextUrl : '';
      if (nextUrlRaw) {
        this.dataset.nextUrl = nextUrlRaw;
      } else {
        this.dataset.nextUrl = '';
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.sentinel) this.sentinel.hidden = true;
      }

      window.history.replaceState({}, '', fetchUrl.pathname);

      document.dispatchEvent(new CustomEvent('infinite-scroll:loaded', { detail: { url: fetchUrl.toString() } }));
    } catch (error) {
      console.error('[infinite-scroll]', error);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    } finally {
      this.loading = false;
      if (this.spinner) this.spinner.hidden = true;
      // Re-check in case the newly appended content still leaves the
      // sentinel within range (short pages / large screens).
      window.requestAnimationFrame(() => this.checkPosition());
    }
  }
}

if (!customElements.get('infinite-scroll-list')) {
  customElements.define('infinite-scroll-list', InfiniteScrollList);
}
